/**
 * The SIM estate (task B3).
 *
 * **Every identifier is rendered in full** (§7, criterion 6). MSISDN and ICCID
 * are the *customer's own* identifiers, and masking one is a defect rather than
 * a privacy measure: an operator holding a SIM and matching it to a row cannot
 * do that against `••••1234`, and the panel exists for exactly that task.
 *
 * The status column shows whatever the upstream called it (§13.8) — the wire
 * spelling is undocumented, so inventing a display mapping would be a guess
 * that silently mislabels a status the day it changes.
 */

import type { PlanSummary, SimSummary } from '@rd/shared';
import { ListChecks, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider.js';
import { Badge } from '../../components/Badge.js';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Field } from '../../components/Field.js';
import { Checkbox, Select } from '../../components/Input.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Pagination } from '../../components/Pagination.js';
import { Table, type Column } from '../../components/Table.js';
import { BulkActionBar } from '../sims/ActionBar.js';
import { actionsFor } from '../sims/actions/actionRegistry.js';
import { simStatusLabel, simStatusTone } from '../sims/statusTone.js';

import { useSelection } from './selection.js';
import { SimFilters } from './SimFilters.js';
import {
  allBasketsOption,
  useBaskets,
  useBasketSims,
  usePlans,
  type SimFilterState,
} from './useInventory.js';

/** The filter keys that round-trip through the URL — the API's own names. */
const FILTER_PARAMS = ['simStatus', 'simFilterType', 'filterValue', 'brandName'] as const;

/**
 * The same timestamp shape the other lists in this product use — the user
 * list, the exports list and the job orders table each carry this function
 * verbatim. An em dash for absent or unparseable, never a bare `Invalid Date`.
 */
function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

export function SimListPage(): ReactElement {
  const auth = useAuth();
  const { tenant } = auth;
  const [params, setParams] = useSearchParams();
  const selection = useSelection();

  const baskets = useBaskets();

  /**
   * The picker's choices: the merged estate first, then the baskets it merges.
   *
   * "All baskets" leads, and is therefore also the default, because it is the
   * answer to the question this screen is usually opened with — where is this
   * SIM. Opening on one basket of three quietly presents a third of the estate
   * as the whole of it, with nothing on screen to say so. A tenant holding a
   * single basket sees exactly what it saw before: no merged option, no picker.
   */
  const basketOptions = useMemo(() => {
    const items = baskets.data?.data ?? [];
    return items.length > 1 ? [allBasketsOption(items), ...items] : items;
  }, [baskets.data]);

  const basketRef = params.get('basket') ?? basketOptions[0]?.basketRef ?? null;

  /**
   * Filters live in the **URL**, not component state (§A PR-5, the
   * `JobListPage` rule): "the AVAILABLE SIMs in this basket" has to survive
   * being pasted into a support thread, and the activation entry points link
   * here with a filter already applied.
   */
  const rawType = params.get('simFilterType');
  const filters: SimFilterState = {
    simStatus: params.get('simStatus') ?? undefined,
    // A shared link may still carry the old ICCID value, which the gateway
    // refuses; dropping anything but the two working spellings means a pasted
    // URL degrades to the automatic search — the value the sender was looking
    // for is still answered, against both fields, instead of returning a 400.
    simFilterType: rawType === 'MSISDN' || rawType === 'SIM_NO' ? rawType : undefined,
    filterValue: params.get('filterValue') ?? undefined,
    brandName: params.get('brandName') ?? undefined,
  };

  const setFilters = useCallback(
    (next: SimFilterState) => {
      const updated = new URLSearchParams(params);
      for (const key of FILTER_PARAMS) {
        const value = next[key];
        if (value === undefined || value === '') updated.delete(key);
        else updated.set(key, value);
      }
      // A new filter is a new result set. Keeping `page=4` would land the user
      // on an empty page and look like "there are no SIMs" (JobListPage rule).
      updated.delete('page');
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  // The selected basket's own counts decide which statuses the filter offers —
  // for the merged view, the summed counts of everything in it.
  const selectedBasket = basketOptions.find((b) => b.basketRef === basketRef) ?? null;

  const sims = useBasketSims(basketRef, filters);
  // GET /plans answers 403 for a default-plan tenant, by design — do not ask.
  // `tenant != null` matters: until /auth/me resolves the tenant is undefined,
  // and a bare `!== true` fires the request anyway — a guaranteed 403 on every
  // page load for exactly the tenants this is meant to spare.
  const plans = usePlans({ enabled: tenant != null && tenant.hasDefaultPlan !== true });

  const rows = useMemo(() => sims.data?.data ?? [], [sims.data]);

  // Remember what the user has seen, so a selection spanning pages can still
  // list every MSISDN in a confirmation dialog after they page away.
  useEffect(() => {
    selection.remember(rows);
  }, [rows, selection]);

  // §05.7: under SUPPRESS the API nulls these, and an empty column is worse
  // than none — it advertises that something was hidden.

  // A tenant activating on a configured default plan is sent no plan fields at
  // all, so the column is omitted rather than rendered empty.
  const hidesPlans = tenant?.hasDefaultPlan === true;

  const columns: Column<SimSummary>[] = [
    {
      key: 'select',
      header: 'Select',
      // §16.5 `select` kind: a gutter sized to the control, centred, with the
      // header text kept for screen readers only. At 25 rows the 20px the old
      // 3.5rem column spent on nothing was 20px the identifiers did not have.
      kind: 'select',
      width: '2.75rem',
      render: (sim) => (
        <Checkbox
          checked={selection.selected.has(sim.simRef)}
          disabled={!selection.canSelect(sim.simRef)}
          aria-label={`Select ${sim.msisdn ?? sim.simRef}`}
          onChange={() => selection.toggle(sim.simRef)}
        />
      ),
    },
    {
      key: 'msisdn',
      header: 'Mobile number',
      // §16.5: identifiers are monospace so a human can compare them digit by
      // digit against the SIM in their hand.
      kind: 'mono',
      render: (sim) => (
        // Underlined on hover and focus rather than permanently. Twenty-five
        // permanently underlined identifiers put a rule under every digit in
        // the column an operator is reading character by character; the colour
        // already says it is a link, and the underline arrives when the pointer
        // does.
        <Link
          to={`/sims/${sim.simRef}`}
          className="rounded-[var(--radius-sm)] font-medium text-action underline-offset-[3px] hover:underline focus-visible:underline"
        >
          {sim.msisdn ?? '—'}
        </Link>
      ),
    },
    { key: 'iccid', header: 'SIM number', kind: 'mono', render: (sim) => sim.iccid ?? '—' },
    /*
      IMSI sits beside the other two identifiers because operators match all
      three against what the network and the device report. It is monospace for
      the same reason as the rest: these are compared digit by digit.
    */
    { key: 'imsi', header: 'IMSI', kind: 'mono', render: (sim) => sim.imsi ?? '—' },
    {
      key: 'status',
      header: 'Status',
      kind: 'status',
      width: '8.5rem',
      // §16.4: never brand-coloured, and the label is always present — colour
      // is never the only channel.
      //
      // `size="sm"` is the size Badge documents for exactly this position: a
      // pill with a button's padding inside a 40px row reads as a control an
      // operator can press, and a status that looks pressable on a screen of
      // 25 SIMs is an invitation to click it.
      render: (sim) => (
        <Badge size="sm" tone={simStatusTone(sim.status)}>
          {simStatusLabel(sim.status)}
        </Badge>
      ),
    },
    {
      /*
        When this SIM started carrying traffic — the date billing began.

        `activatedAt` is null for everything that has not been activated, which
        is most of this estate, so the cell reads as plainly empty rather than
        as a date that failed to load. It is not a `kind: 'mono'` column: a
        date is read, not compared digit by digit against a SIM in someone's
        hand, which is what that treatment is for.
      */
      key: 'activatedAt',
      header: 'Activated',
      width: '11rem',
      render: (sim: SimSummary) => formatWhen(sim.activatedAt),
    },
    /*
      The Plan column exists only for tenants that choose plans. When the
      account activates on a configured default the API strips plan fields
      entirely (respond() → applyPlanSuppression), so rendering the column
      would show a full height of em-dashes and advertise a concept the
      customer deliberately does not have.
    */
    ...(hidesPlans
      ? []
      : [
          {
            key: 'plan',
            header: 'Plan',
            render: (sim: SimSummary) => sim.planName ?? sim.planCode ?? '—',
          },
        ]),
    {
      /*
        Swap is single-SIM by construction (`bulk: false`), so it can never
        appear in the bulk bar. Without a row entry it would be reachable only
        by opening a SIM first. This link is the estate's way in — same screen,
        same URL as the one SIM detail links to.

        Absent, not disabled (criterion 15): `actionsFor` applies permission,
        feature flag and status eligibility, so a row that cannot be swapped
        shows nothing rather than a dead control.
      */
      key: 'row-actions',
      header: 'Actions',
      kind: 'actions',
      width: '7rem',
      /*
        Every action this SIM is actually eligible for, not just swap. An
        un-activated SIM's whole purpose on this screen is to be activated, so
        making the operator select it and find the bulk bar to do the one thing
        it can do was the wrong shape.

        Absent, not disabled (criterion 15): `actionsFor` applies permission,
        feature flag and status eligibility, so a row shows only what it can do
        and an ineligible row shows nothing rather than a dead control.
      */
      render: (sim) => {
        const actions = actionsFor(sim, auth);
        if (actions.length === 0) return null;
        return (
          // Right-set and never revealed on hover: a control that appears only
          // under the pointer is a control a keyboard or touch user cannot find,
          // and the whole point of this column is that a row shows what it can
          // do. The small caps-height label reads as an action rather than as a
          // second piece of data in the row.
          <span className="flex items-center justify-end gap-[var(--space-3)]">
            {actions.map((action) => (
              <Link
                key={action.key}
                /*
                  Both are per-SIM routes that take their subject from the path.
                  Activate used to link to `/sims/activate?sim=<ref>`, which
                  nothing read — the operator landed on the picker and had to
                  find the SIM they had just clicked.
                */
                to={`/sims/${sim.simRef}/${action.key === 'swap' ? 'swap' : 'activate'}`}
                data-testid={`row-${action.key}`}
                aria-label={`${action.label} for ${sim.msisdn ?? sim.simRef}`}
                /*
                  Buttons, not text links. These are the operations this panel
                  exists to perform, and a row of underlined words read as more
                  identifiers in a row already full of them.

                  Three treatments, and the row reads its own registry for
                  which — the same rule `ActionBar` applies on the SIM detail
                  page, so one SIM's actions do not change colour depending on
                  which screen they are seen from. Activate is green because it
                  is the safe, expected operation. Anything the registry marks
                  `destructive` — swap today — is red: it takes a working SIM
                  out of service, and the estate is where an operator is most
                  likely to click one by mistake, scanning a page of near
                  identical rows.
                */
                className={`inline-flex h-7 items-center whitespace-nowrap rounded-[var(--radius-sm)] px-[var(--space-3)] text-[length:var(--text-xs)] font-medium shadow-[var(--elevation-1)] transition-[filter,background-color] duration-[var(--motion-fast)] ${
                  action.key === 'activate'
                    ? 'bg-action text-action-fg hover:brightness-95'
                    : action.destructive
                      ? 'bg-danger text-danger-fg hover:brightness-95'
                      : 'border border-line bg-surface text-body hover:border-line-strong hover:bg-surface-alt'
                }`}
              >
                {action.label}
              </Link>
            ))}
          </span>
        );
      },
    },
    /*
      No Network column. Every SIM on this account is served by one operator, so
      it repeated a single word down the page and bought nothing; the value is
      on the SIM detail screen for the rare case it matters.
    */
  ];

  /*
    The basket picker, rendered into the filter bar rather than floating above
    it as a bare `<select>` of its own.

    It is the widest-reaching control on the screen — it decides which 5,000 of
    the estate's SIMs the table is about — and it used to be the only unstyled
    control in the product: a raw element with its own radius, its own padding
    and a text label sitting loose on the canvas. Inside the group it takes the
    same 36px shape as every other control, and an operator reads one bar of
    things that change the table instead of a bar plus a stray.
  */
  const basketPicker =
    basketOptions.length > 1 ? (
      <Field label="Basket">
        {(props) => (
          <Select
            {...props}
            aria-label="Basket"
            value={basketRef ?? ''}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              next.set('basket', e.target.value);
              next.set('page', '1');
              setParams(next, { replace: true });
            }}
          >
            {basketOptions.map((b) => (
              <option key={b.basketRef} value={b.basketRef}>
                {b.basketName ?? b.basketRef} ({b.counts.total})
              </option>
            ))}
          </Select>
        )}
      </Field>
    ) : null;

  return (
    <>
      <PageHeader
        title="SIM estate"
        description={basketRef ? undefined : 'Choose a basket to see its SIMs.'}
      />

      <SimFilters
        sims={rows}
        basket={selectedBasket}
        value={filters}
        onChange={setFilters}
        leading={basketPicker}
      />

      {sims.isError ? <ErrorState error={sims.error} onRetry={() => void sims.refetch()} /> : null}

      {sims.data || sims.isPending ? (
        <>
          {/*
            The strip between the filter bar and the table: what you can do to
            the rows, and what you have already done to them.

            It sits tight against the table's top edge (`mb-[var(--space-2)]`)
            so it reads as part of that object rather than as a third band on
            the page, and the count is right-set so the eye finds it in the same
            place whether or not anything is selected.
          */}
          <div className="mb-[var(--space-2)] flex flex-wrap items-center justify-between gap-[var(--space-3)]">
            <Button
              variant="ghost"
              size="sm"
              icon={ListChecks}
              // Disabled until there are rows to select. Clicking it while the
              // page is still loading selected nothing and said nothing — a
              // silent no-op, and the operator's next action is a bulk one.
              disabled={rows.length === 0}
              onClick={() => selection.selectPage(rows.map((s) => s.simRef))}
            >
              {/* Only the rows currently rendered. Never across pages, and never
                  rows the user has not seen (§05.5). */}
              Select all on this page
            </Button>
            {selection.count > 0 ? (
              <span className="text-[length:var(--text-xs)] tabular-nums text-muted">
                <span className="font-medium text-body">{selection.count}</span> selected
              </span>
            ) : null}
          </div>

          <Table
            caption="SIMs in this basket"
            columns={columns}
            rows={rows}
            rowKey={(sim) => sim.simRef}
            loading={sims.isPending}
            isSelected={(sim) => selection.selected.has(sim.simRef)}
            empty={
              <EmptyState
                title="No SIMs here"
                body="Nothing in this basket matches the current filters."
              />
            }
          />

          {sims.data ? (
            <Pagination meta={sims.data.meta} onPageChange={sims.setPage} label="SIMs" />
          ) : null}
        </>
      ) : null}

      {selection.capMessage ? (
        <p
          role="status"
          data-testid="cap-message"
          className="mt-[var(--space-3)] flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-warning/20 bg-warning-soft px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-sm)] text-warning-text"
        >
          {/* The glyph, not the hue, is what carries "this is a limit" to a
              reader who does not see the amber — the same icon-plus-words
              pairing the pills use, for the same WCAG 1.4.1 reason. */}
          <TriangleAlert
            aria-hidden="true"
            size={14}
            strokeWidth={2.25}
            className="mt-[2px] shrink-0"
          />
          <span>{selection.capMessage}</span>
        </p>
      ) : null}

      <BulkActionBar plans={(plans.data ?? []) as PlanSummary[]} />
    </>
  );
}
